import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import Alert from "veneer/Alert";
import Button from "veneer/Button";
import MessageDialog from "veneer/MessageDialog";

import {
  MEDIA_ENTRY_CONTENT_GROUP,
  MEDIA_ENTRY_FOLDER,
  MEDIA_ENTRY_USER_CONTENT,
  STATIC_CONTENT_TYPES,
} from "cortex/ui/src/fleet/constant/mediaLibrary";
import { contentFoldersSelector } from "cortex/ui/src/fleet/redux-store/content-folders/selectors";
import { contentGroupsSelector } from "cortex/ui/src/fleet/redux-store/content-groups/selectors";
import { FormData } from "cortex/ui/src/fleet/view/device/model";
import { getTableData } from "cortex/ui/src/fleet/view/media-library/utils";
import { userContentsSelector } from "cortex/ui/src/redux-store/user-contents/selectors";

import AddSpotModal from "./AddSpotModal";
import SystemStrategySpotTable from "./SystemStrategySpotTable";
import { Spot } from "../model";

interface FallbackContentSelectorProps {
  fallbackContent?: FormData["fallbackContents"];
  readOnly?: boolean;
  setFallbackContent: (nextValue: FormData["fallbackContents"]) => void;
}

export default function FallbackContentSelector({
  fallbackContent = [],
  readOnly = false,
  setFallbackContent,
}: FallbackContentSelectorProps) {
  type FallbackContent = NonNullable<FormData["fallbackContents"]>[number];

  const contentFolders = useSelector(contentFoldersSelector);
  const contentGroups = useSelector(contentGroupsSelector);
  const userContents = useSelector(userContentsSelector);
  const [showModal, setShowModal] = useState(false);
  const [rows, setRows] = useState<ReadonlyArray<Spot>>([]);
  const [addedSpot, setAddedSpot] = useState<Spot | null>(null);

  const allRows = useMemo(() => {
    return getTableData({
      contentFolders,
      contentGroups,
      includeArchived: true,
      includeMediaTypes: [
        MEDIA_ENTRY_FOLDER,
        MEDIA_ENTRY_USER_CONTENT,
        MEDIA_ENTRY_CONTENT_GROUP,
      ],
      userContents,
    }) as ReadonlyArray<Spot>;
  }, [contentFolders, contentGroups, userContents]);

  useEffect(() => {
    if (!allRows) return;

    const entries: Spot[] = fallbackContent.flatMap((fc, index) => {
      const contentId = fc.contentId;

      const mediaEntry = allRows.find(
        (ar) => ar.id === contentId && ar.type === fc.type,
      );

      return mediaEntry ? [{ ...(mediaEntry as Spot), _rowNum: index }] : [];
    });

    setRows(entries);
  }, [allRows, fallbackContent]);

  const handleRowsChange = useCallback(
    (nextRows: ReadonlyArray<Spot>) => {
      setRows(nextRows);
      setFallbackContent(
        nextRows.map((row) => ({
          contentId: row.id,
          duration: row.duration,
          id: row.id,
          ...(row.conflictsWith ? { conflicts_with: row.conflictsWith } : {}),
          type: row.type,
        })) as ReadonlyArray<FallbackContent>,
      );
    },
    [setFallbackContent],
  );

  const handleAddNewRows = useCallback(
    (newRows: ReadonlyArray<Spot>) => {
      setRows((prev) => {
        const combinedRows = [...prev, ...newRows];
        handleRowsChange(combinedRows);
        return combinedRows;
      });
      setAddedSpot(newRows[0]);
    },
    [handleRowsChange],
  );

  useEffect(() => {
    if (!addedSpot) return;

    const timeoutId = window.setTimeout(() => {
      setAddedSpot(null);
    }, 3000);

    return () => window.clearTimeout(timeoutId);
  }, [addedSpot]);

  return (
    <>
      <Button
        disabled={readOnly}
        icon="add"
        onClick={() => setShowModal(true)}
        variant="outlined"
      >
        Add Fallback Creative(s)
      </Button>
      <SystemStrategySpotTable
        handleRowsChange={handleRowsChange}
        readOnly={readOnly}
        rows={rows}
      />
      <MessageDialog
        dismissLabel="Close"
        onDismiss={() => setShowModal(false)}
        open={showModal}
        title={"Add Fallback Creative(s)"}
        variant="modal-lg"
      >
        {addedSpot && (
          <Alert severity="success">
            Successfully added <b>{addedSpot.name}</b> to the fallback
            creatives.
          </Alert>
        )}
        <AddSpotModal
          handleSystemStrategyChange={handleAddNewRows}
          priority="fallback"
          validContentTypes={STATIC_CONTENT_TYPES as any}
        />
      </MessageDialog>
    </>
  );
}
